import { ChangeDetectionStrategy, Component, HostBinding, Inject, Input, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';

import { EpcotConfig } from '@frontend/sports/common/client-config-data-access';
import { NumpadSource, StakeChangedEvent } from 'packages/sports/web/app/src/numpad/model';
import { NumpadService } from 'packages/sports/web/app/src/numpad/numpad.service';
import { Observable, Subscription, combineLatest, distinctUntilChanged, filter, map } from 'rxjs';

import { ILinearBetslipMessages } from '../../../../../base/models/betslip-messages';
import {
    ILinearBetslipMessagesProviderService,
    LINEAR_BETSLIP_MESSAGES_PROVIDER_INJECTION_TOKEN,
} from '../../../../../base/services/betslip-messages-provider.service';
import { parseStakeFieldStake } from '../../../../../modules/stake/utils';
import { FreeBetToken } from '../../../../reward-tokens/reward-tokens.model';
import StakeConvertService from '../../../../stake/services/stake-convert.service';
import { IStake } from '../../../models';
import { ILinearContainerStakeService, LINEAR_CONTAINER_STAKE_SERVICE } from '../../../services/digital/linear/linear-container-stake.interface';
import { LinearFocusPlaceholderService } from '../../../services/digital/linear/linear-focus-placeholder.service';

@Component({
    selector: 'bs-linear-container-stake',
    templateUrl: './linear-container-stake.component.html',
    styleUrls: ['./linear-container-stake.component.scss'],
    // eslint-disable-next-line @angular-eslint/use-component-view-encapsulation
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export default class LinearContainerStakeComponent implements OnInit, OnDestroy {
    @Input() isDisabled: boolean;
    @HostBinding('class') className = 'linear-container-stake';

    numpadSource = NumpadSource.BetSlip;

    messages: ILinearBetslipMessages;

    private stakeClickedSubscription?: Subscription;

    stake$: Observable<IStake | null>;
    hasStakeError$: Observable<boolean>;
    freeBetToken$: Observable<FreeBetToken | null>;
    readonly useStakeFieldCursor: boolean;

    constructor(
        @Inject(LINEAR_BETSLIP_MESSAGES_PROVIDER_INJECTION_TOKEN) betslipMessageProviderService: ILinearBetslipMessagesProviderService,
        @Inject(LINEAR_CONTAINER_STAKE_SERVICE) private linearContainerStakeService: ILinearContainerStakeService,
        private numpadService: NumpadService,
        private stakeConvert: StakeConvertService,
        private focusPlaceholderService: LinearFocusPlaceholderService,
        public epcotConfig: EpcotConfig,
    ) {
        this.messages = betslipMessageProviderService.getMessages();
        this.useStakeFieldCursor = this.epcotConfig.isEnabled;
    }

    ngOnDestroy(): void {
        this.stakeClickedSubscription?.unsubscribe();
    }

    ngOnInit(): void {
        this.stake$ = this.linearContainerStakeService.getStake$();

        const isNumpadActive$ = this.numpadService.state$.pipe(
            map((state) => state.isOpened),
            distinctUntilChanged(),
        );

        const stakeClicked$ = combineLatest([this.stake$, isNumpadActive$]).pipe(
            distinctUntilChanged(([_, numpadWasActive], [__, numpadIsActive]) => numpadIsActive === numpadWasActive),
            filter(([_, numpadIsActive]) => numpadIsActive),
            map(([stake, _]) => stake),
        );

        this.stakeClickedSubscription = stakeClicked$.subscribe((stake) => this.linearContainerStakeService.clickStake(stake));

        this.hasStakeError$ = this.linearContainerStakeService.getStakeErrors$().pipe(map((errors) => !!errors?.hasStakeErrors));

        this.freeBetToken$ = this.linearContainerStakeService.getFreeBetToken$();
    }

    setStake(event: StakeChangedEvent) {
        const stakeStr = event.stake;
        const stake = parseStakeFieldStake(stakeStr);

        this.linearContainerStakeService.setStake(stakeStr, stake);
    }

    get keypadId(): string {
        return this.linearContainerStakeService.getKeypadId();
    }

    get adornmentText$(): Observable<string | undefined> {
        return this.linearContainerStakeService.getAdornmentText();
    }

    getStakeAsStr(stake: number | null): string {
        return this.stakeConvert.convert(stake);
    }

    get focusPlaceholder(): string | undefined {
        return this.focusPlaceholderService.focusPlaceholder;
    }
}
